import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import DataTable from '../components/DataTable';
import { countriesAPI } from '../services/mastersAPI';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const Countries = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryToDelete, setCountryToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    code3: '',
    phoneCode: '',
    currency: '',
    flag: '',
    isActive: true
  });

  useEffect(() => {
    fetchCountries();
  }, [currentPage, searchTerm]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await countriesAPI.getCountries({
        page: currentPage,
        limit: 10,
        search: searchTerm
      });
      setCountries(response.countries);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCountry = async (e) => {
    e.preventDefault();
    try {
      await countriesAPI.createCountry(formData);
      toast.success('Country created successfully');
      setShowAddModal(false);
      resetForm();
      fetchCountries();
    } catch (error) {
      console.error('Error creating country:', error);
      toast.error(error.response?.data?.message || 'Failed to create country');
    }
  };

  const handleEditCountry = async (e) => {
    e.preventDefault();
    try {
      await countriesAPI.updateCountry(selectedCountry._id, formData);
      toast.success('Country updated successfully');
      setShowEditModal(false);
      resetForm();
      setSelectedCountry(null);
      fetchCountries();
    } catch (error) {
      console.error('Error updating country:', error);
      toast.error(error.response?.data?.message || 'Failed to update country');
    }
  };

  const handleDeleteCountry = (country) => {
    setCountryToDelete(country);
    setShowDeleteModal(true);
  };

  const confirmDeleteCountry = async () => {
    if (!countryToDelete) return;
    
    try {
      await countriesAPI.deleteCountry(countryToDelete._id);
      toast.success('Country deleted successfully');
      fetchCountries();
    } catch (error) {
      console.error('Error deleting country:', error);
      toast.error(error.response?.data?.message || 'Failed to delete country');
    } finally {
      setShowDeleteModal(false);
      setCountryToDelete(null);
    }
  };

  const handleViewCountry = (country) => {
    setSelectedCountry(country);
    setShowViewModal(true);
  };

  const handleEditClick = (country) => {
    setSelectedCountry(country);
    setFormData({
      name: country.name,
      code: country.code,
      code3: country.code3,
      phoneCode: country.phoneCode,
      currency: country.currency,
      flag: country.flag || '',
      isActive: country.isActive
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      code3: '',
      currency: '',
      phoneCode: '',
      flag: '',
      isActive: true
    });
  };

  const columns = [
    {
      key: 'flag',
      header: 'Flag',
      render: (value) => value ? (
        <span className="text-2xl">{value}</span>
      ) : (
        <GlobeAltIcon className="h-6 w-6 text-gray-400" />
      )
    },
    { key: 'name', header: 'Country Name' },
    { key: 'code', header: 'Code (2)' },
    { key: 'code3', header: 'Code (3)' },
    { key: 'phoneCode', header: 'Phone Code' },
    { key: 'currency', header: 'Currency' },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, item) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewCountry(item);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 rounded-lg hover:bg-blue-50 transition-colors duration-200"
            title="View"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(item);
            }}
            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-lg hover:bg-indigo-50 transition-colors duration-200"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCountry(item);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded-lg hover:bg-red-50 transition-colors duration-200"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  if (loading && countries.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading countries..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Countries Master
                </h1>
                <p className="mt-2 text-gray-600 text-lg">
                  Manage countries for global operations and company locations
                </p>
              </div>
              <div className="flex-shrink-0">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Country
                </motion.button>
              </div>
            </div>
          </div>

        {/* Search */}
        <div className="mt-6">
          <div className="flex items-center">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Countries Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100/50">
            <h2 className="text-lg font-semibold text-gray-900">Countries</h2>
            <p className="text-sm text-gray-600 mt-1">Manage countries for global operations</p>
          </div>

          <div className="overflow-x-auto">
            {/* Card Grid Layout */}
            {countries.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {countries.map((country, index) => (
                  <motion.div
                    key={country._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl border border-white/50 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 relative overflow-hidden">
                      <div className="absolute inset-0 bg-black/10"></div>
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                            <span className="text-2xl">
                              {country.flag || '🌍'}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">{country.name}</h3>
                            <p className="text-white/80 text-xs">Country</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            country.isActive
                              ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30'
                              : 'bg-gray-500/20 text-gray-100 border border-gray-400/30'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                              country.isActive ? 'bg-emerald-400' : 'bg-gray-400'
                            }`}></div>
                            {country.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-4 space-y-3">
                      {/* Country Details */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Code (2)</p>
                          <p className="text-gray-900 font-semibold">{country.code || '-'}</p>
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Code (3)</p>
                          <p className="text-gray-900 font-semibold">{country.code3 || '-'}</p>
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Phone Code</p>
                          <p className="text-gray-900 font-semibold">{country.phoneCode ? `+${country.phoneCode}` : '-'}</p>
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Currency</p>
                          <p className="text-gray-900 font-semibold">{country.currency || '-'}</p>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2">
                        <span>Created {new Date(country.createdAt).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="px-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleViewCountry(country)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-xs font-medium"
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                            <span>View</span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleEditClick(country)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors duration-200 text-xs font-medium"
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                            <span>Edit</span>
                          </motion.button>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteCountry(country)}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200 text-xs font-medium"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          <span>Delete</span>
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                  <GlobeAltIcon className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No countries found' : 'No countries yet'}
                </h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                  {searchTerm
                    ? 'Try adjusting your search terms or clear the search to see all countries.'
                    : 'Get started by adding countries to support global operations.'
                  }
                </p>
                {!searchTerm && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      resetForm();
                      setShowAddModal(true);
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Your First Country
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Add Country Modal */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowAddModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleAddCountry}>
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <PlusIcon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Add New Country</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Country Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="Enter country name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          2-Letter Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="2"
                          value={formData.code}
                          onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="US"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          3-Letter Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="3"
                          value={formData.code3}
                          onChange={(e) => setFormData({...formData, code3: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="USA"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Code *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.phoneCode}
                          onChange={(e) => setFormData({...formData, phoneCode: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="+1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="3"
                          value={formData.currency}
                          onChange={(e) => setFormData({...formData, currency: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="USD"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Flag Emoji
                        </label>
                        <input
                          type="text"
                          value={formData.flag}
                          onChange={(e) => setFormData({...formData, flag: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="🇺🇸"
                        />
                      </div>
                    </div>
                    <div className="flex items-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isActive" className="ml-3 block text-sm font-medium text-gray-900">
                        Active Country
                      </label>
                    </div>
                  </div>

                  <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 border border-transparent rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                    >
                      Create Country
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Country Modal */}
        <AnimatePresence>
          {showEditModal && selectedCountry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowEditModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleEditCountry}>
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <PencilIcon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Edit Country</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Country Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          2-Letter Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="2"
                          value={formData.code}
                          onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          3-Letter Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="3"
                          value={formData.code3}
                          onChange={(e) => setFormData({...formData, code3: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Code *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.phoneCode}
                          onChange={(e) => setFormData({...formData, phoneCode: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="3"
                          value={formData.currency}
                          onChange={(e) => setFormData({...formData, currency: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Flag Emoji
                        </label>
                        <input
                          type="text"
                          value={formData.flag}
                          onChange={(e) => setFormData({...formData, flag: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <input
                        type="checkbox"
                        id="editIsActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        className="h-5 w-5 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                      />
                      <label htmlFor="editIsActive" className="ml-3 block text-sm font-medium text-gray-900">
                        Active Country
                      </label>
                    </div>
                  </div>

                  <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-yellow-500 to-orange-500 border border-transparent rounded-xl hover:from-yellow-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                    >
                      Update Country
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Country Modal */}
        <AnimatePresence>
          {showViewModal && selectedCountry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowViewModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-green-500 to-teal-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <EyeIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Country Details</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setShowViewModal(false);
                          handleEditClick(selectedCountry);
                        }}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
                      >
                        <PencilIcon className="h-5 w-5 text-white" />
                      </motion.button>
                      <button
                        type="button"
                        onClick={() => setShowViewModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  {/* Country Header Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-100"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="text-4xl">
                          {selectedCountry.flag || '🌍'}
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">{selectedCountry.name}</h2>
                          <p className="text-gray-600 text-sm">
                            {selectedCountry.code} • {selectedCountry.code3}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                        selectedCountry.isActive 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          selectedCountry.isActive ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        {selectedCountry.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </motion.div>

                  {/* Country Information Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Basic Information
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Country Name</span>
                          <span className="text-sm font-semibold text-gray-900">{selectedCountry.name}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">2-Letter Code</span>
                          <span className="text-sm font-semibold text-gray-900 font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {selectedCountry.code}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">3-Letter Code</span>
                          <span className="text-sm font-semibold text-gray-900 font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {selectedCountry.code3}
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Contact & Currency
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Phone Code</span>
                          <span className="text-sm font-semibold text-gray-900 font-mono bg-green-100 text-green-800 px-2 py-1 rounded">
                            {selectedCountry.phoneCode}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Currency</span>
                          <span className="text-sm font-semibold text-gray-900 font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            {selectedCountry.currency}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Created</span>
                          <span className="text-sm text-gray-900">
                            {new Date(selectedCountry.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && countryToDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <TrashIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Delete Country
                    </h3>
                    <p className="text-red-100 mb-4">
                      Are you sure you want to delete <strong className="text-white">{countryToDelete.name}</strong>?
                    </p>
                    
                    <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-4 mb-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-300 mr-3 flex-shrink-0" />
                        <div className="text-left">
                          <p className="text-white font-semibold">Warning!</p>
                          <p className="text-red-100 text-sm">This will remove the country from all plans and companies. This action cannot be undone.</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-red-200 text-sm">
                      This action is permanent and cannot be reversed.
                    </p>
                  </div>
                </div>

                <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={confirmDeleteCountry}
                    className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-pink-500 border border-transparent rounded-xl hover:from-red-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Delete Country
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

export default Countries;